<template>
	<div :class="getChartClass">
		<h3 v-if="heading" v-html="heading"></h3>
		<div v-if="hasData" class="grid grid--bar-chart bar-chart__list">
			<div v-if="legend.length > 0"></div>
			<ul v-if="legend.length > 0" class="bar-chart__legend">
				<li v-for="item in legend">
					<span :id="'legend-' + item" v-html="item"></span>
				</li>
			</ul>
			<template v-for="(item, key) in data.data" class="bar-chart__item">
				<div class="bar-chart__label">
					<span :id="getBarId(key)" v-html="getBarLabel(item, key)" class="bar-chart__stat"></span>
					<Icon v-if="social" :icon="getBarIcon(key)"></Icon>
				</div>
				<div class="bar-chart__bars" :aria-labelledby="getBarId(key)">
					<div v-for="(value, index) in item" :class="getBarClass(value, index, item)" :style="getBarStyle(value, index)" :aria-labelledby="getLegendAriaLabel(legend[index])">
						<span class="bar-chart__bar"></span>
						<span v-html="getBarPercentage(item, key, value)" class="bar-chart__percent"></span>
					</div>
				</div>
			</template>
			<div v-if="scale.length > 0" :class="getScaleClass()">
				<span v-for="(value, index) in scale" class="bar-chart__scale-line" :style="getScaleLineStyle(value)">
					<span v-text="value"></span>
				</span>
			</div>
		</div>
		<div v-else class="bar-chart__empty">
			<p v-html="_translate('ui.nodata')"></p>
		</div>
	</div>
</template>

<script>

	import Icon from '@/components/Icon.vue'

	export default {

		components: {
			Icon
		},

		props: {

			heading: {
				type: String,
				default: '',
			},

			data: {
				type: Object,
				default: () => {}
			},

			legend: {
				type: Array,
				default: () => []
			},

			scale: {
				type: Array,
				default: () => []
			},

			color: {
				type: String,
				default: 'red'
			},

			basis: {
				type: Number,
				default: 70
			},

			percentage: {
				type: Boolean,
				default: false
			},

			social: {
				type: Boolean,
				default: false
			},

			nbh: {
				type: Boolean,
				default: false
			}

		},

		computed: {

			getChartClass: function() {

				var c = "bar-chart bar-chart--" + this.color;
				return c;

			},

			hasData: function() {
				const keys = Object.keys(this.data);
				return (keys.length > 0);
			}

		},

		methods: {

			getTranslationKey(key) {

				var k = key.replace(/ /g, '_');

				k = k.replace(/\//g, '_');

				if ( k.substr(0, 1) !== '_' ) {
					k = '_' + k
				}

				return k;

			},

			getBarWidth: function(item) {

				return ((item/this.basis) * 100)

			},

			getBarId: function(key) {

				return 'bar-' + key.replace(/_/g, '').toLowerCase();

			},

			getBarStyle: function(value, index) {

				return 'width:' + (this.getBarWidth(value)+.5) + '%';

			},

			getBarClass: function(value, index, item) {

				var c = 'bar-chart__bar-block ';
				if ( item.length > 1) {
					c += ' multiple bar-' + (index + 1);
				}
				return c;

			},

			getBarLabel: function(item, key) {

				var label_string = this._translate('barchart.label.' + this.getTranslationKey(key));

				if ( label_string === '[undefined string]' ) {
					label_string = this.apiLabel(key);
				}

				var label = this.social ?
					'' :
					label_string;

				if ( this.nbh ) {
					label = label.replace(/\-/g, '&#8209;');
				}

				return label;

			},

			getLegendAriaLabel: function(key) {
				return (key) ? 'legend-' + key : false;
			},

			getBarPercentage: function(item, key, index) {

				if ( this.data.total && this.calculatePercentage ) {
					index = index/this.data.total * 100;
				}	

				var stat = this.percentage ?
					this.formatPercentage(Number(index)/100, 1):
					this.thousandSeparators(index);

				return stat;

			},

			getBarIcon: function(key) {

				var icon = this.apiLabel(key);

				switch (icon) {

					case 'facebook':
						return 'Facebook';

					case 'twitter':
						return 'Twitter';

					case 'linkedin':
						return 'LinkedIn';

					case 'pinterest':
						return 'Pinterest';

				}

				return false;

			},

			getScaleClass: function() {

				var c = 'bar-chart__scale';
				if ( this.legend.length > 0 ) {
					c += ' legend';
				}
				return c;

			},

			getScaleLineStyle: function(value) {

				var s = "left: " + this.getBarWidth(value) + "%;";
				if ( value === 0 ) {
					s += " border-color: transparent";
				}
				return s;

			},

		}

	}

</script>

<style lang="scss">

	$second: .7;

	.bar-chart {

		text-align: left;
		h3 { color:$red; font-weight: 400; }

	}

	.bar-chart--blue {

		h3 { color:$blue-dark; }

		.bar-chart__bar { background: $blue; }

		.bar-chart__percent,
		.bar-chart__legend,
		.bar-chart__stat-highlight { color: $blue-dark; }

		.bar-chart__scale { border-left-color: $blue; }

	}

	.bar-chart--grey {

		h3 { color: #47485A; }

		.bar-chart__bar { background: #47485A; }

		.bar-chart__percent,
		.bar-chart__legend,
		.bar-chart__stat-highlight { color: #47485A; }

		.bar-chart__scale { border-left-color: #47485A; }

	}

	.bar-chart__list {
		list-style-type: none;
		padding: 0;
		margin: 0;
		position: relative;
	}

	.bar-chart__legend {
		color: $red;
		display: flex;
		padding-left: 1.125rem;
		padding-bottom: .5rem;
		li {
			margin-right: 1.5rem;
			&:nth-child(2) {
				opacity: #{$second - 0.1};
			}
			span {
				font-size: .8rem;
				position: relative;
				left: -.35rem;
			}
		}
	}

	.bar-chart__bars {
		margin-bottom: 1rem;
		position: relative;
		z-index: 2;
	}
	.bar-chart__item {
		display: flex;
		align-items: center;
		margin-bottom: .35em;
	}

	.bar-chart__bar-block {
		position: relative;
		background: red;

		&.multiple {
			.bar-chart__bar {
				height: 1.125rem;
			}
			.bar-chart__percent {
				font-size: .8rem;
			}
		}
		&.bar-2 {
			opacity: $second;
		}
	}

	.bar-chart__bar {
		background: $red;
		height: 2.5rem;
		display: block;
	}

	.bar-chart__percent {
		position: absolute;
		left: 100%;
		top: 0;
		bottom: 0;
		padding-left: .75em;
		display: flex;
		align-items: center;
		color: $red;
		font-weight: 600;
	}

	.bar-chart__label {
		text-align: right;
		display: flex;
		align-items: center;
		justify-content: flex-end;
		padding-right: 1.5rem;
		margin-bottom: 1rem;

		.icon {

			margin-left: 8px;

			svg {
				width: 24px;
				height: 24px;
			}
		}
	}

	.bar-chart__stat {
		font-weight: 600;
	}

	.bar-chart__empty {
		font-style: italic;
		letter-spacing: .1em;
		font-size: .85rem;
		opacity: $second;
	}

	.bar-chart__stat-highlight {
		color: $red;
		margin-right: .5em;
		// font-weight: 600;
	}

	.bar-chart__scale {
		width: calc(100% + 2px);
		position: absolute;
		top: -1rem;
		right: 0;
		bottom: 0;
		grid-column-start: 2;
		grid-column-end: 3;
		// background-color: rgba(255,0,0,0.05);
		transform: translate3d(0,0,0);
		border-left: 2px solid $red;

		@include mq(ms) {
			left: 33%;
			width: calc(100% - 33% + 2px);
		}

		&.legend {
			top: 3rem;
		}

		.bar-chart__scale-line {
			position: absolute;
			top: 0;
			left: 50%;
			bottom: 0;
			border-left: 1px solid #eee;
			transform: translate3d(0,0,0);

			> span {
				position: absolute;
				bottom: -1.5rem;
				left: 50%;
				font-size: .8rem;
				transform: translate3d(-50%,0,0);
			}

		}
	}

</style>